import React, { useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

// Register necessary components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const CanaraHomeLoanEmiCal = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal *
        monthlyInterest *
        Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: ((currentMonth + i) % 12) + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === "" ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === "" ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === "" ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ["Principal Amount", "Total Interest"],
    datasets: [
      {
        label: "EMI Breakdown",
        data: [loanAmount, totalInterest],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  const categories = [
    { id: 1, name: "Category 1", link: "/" },
    { id: 2, name: "Category 2", link: "/" },
    { id: 3, name: "Category 3", link: "/" },
    { id: 4, name: "Category 4", link: "/" },
    // Add more categories as needed
  ];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        "question": "Can I use this calculator for floating rate loans?",
        "answer": "Yes, though results are based on the current rate. Floating rates may change over time."
      },
      {
        "question": "Is the EMI value shown final?",
        "answer": "It’s an estimate. Final EMI is based on the lender’s approval and documentation."
      },
      {
        "question": "Can I change my EMI later?",
        "answer": "Yes, through tenure adjustment or part-prepayments."
      },
      {
        "question": "What is the minimum and maximum loan tenure?",
        "answer": "Typically between 5 and 30 years. Canara Bank offers flexible options."
      },
      {
        "question": "Can the calculator simulate top-up loans?",
        "answer": "Not directly, but you can add the top-up amount and recalculate."
      },
      {
        "question": "How often should I check my EMI using this tool?",
        "answer": "Whenever there’s a rate change or when planning prepayment."
      },
      {
        "question": "Does the calculator work for balance transfers?",
        "answer": "Yes, input your new principal and updated rate for EMI insights."
      },
      {
        "question": "Will using this calculator affect my credit score?",
        "answer": "No, it is completely offline and non-intrusive."
      },
      {
        "question": "Can I download the amortisation schedule?",
        "answer": "Yes, our tool allows schedule download for reference."
      },
      {
        "question": "Does the EMI include insurance or taxes?",
        "answer": "No, it only includes principal and interest. Other charges are extra."
      },
      {
        "question": "Is the calculator mobile friendly?",
        "answer": "Absolutely. It’s designed for use on all devices."
      },
      {
        "question": "Can I use it for commercial properties?",
        "answer": "Yes, if you're taking a home loan for commercial construction under housing norms."
      },
      {
        "question": "Are joint loans calculated differently?",
        "answer": "EMI remains the same. Only eligibility may improve with co-applicants."
      },
      {
        "question": "Can I print the EMI result page?",
        "answer": "Yes, a printer-friendly option is available."
      },
      {
        "question": "How frequently is the interest rate updated?",
        "answer": "We update our backend as per Canara Bank’s official rate announcements."
      }
    ]
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
           <title>Canara Home Loan EMI Calculator - Quick & Accurate EMI Estimator</title>
          <meta  name='description' content='Use our Canara home loan EMI Calculator to calculate your monthly payments instantly. Plan your loan better with accurate EMI estimation.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/canara-home-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
        <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">
              Canara Home Loan EMI Calculator
            </span>
          </li>
        </ol>
      </nav> */}
       <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
        <AdBanner displayOrder="1" />
        </div>
      </div>
      <div className="py-4 ">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
        Canara Home Loan EMI Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Amount
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {loanAmount.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Rate of Interest (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{interestRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Tenure (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={loanTenure}
                    onChange={handleLoanTenureChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{loanTenure} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateEMI}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
                <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
                <p>
                  Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}
                </p>
                <p>
                  Total Amount (Principal + Interest): ₹{" "}
                  {parseFloat(totalAmount).toLocaleString()}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
            {amortizationSchedule.length > 0 && (
              <div className="flex flex-col space-y-4">
                {Array.from(
                  new Set(amortizationSchedule.map((item) => item.year))
                ).map((year, idx) => (
                  <div key={idx} className="mb-4 border rounded-md shadow-md">
                    <div
                      className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${
                        activeYear === year ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleYearToggle(year)}
                    >
                      <span>{year}</span>
                      <span>{activeYear === year ? "-" : "+"}</span>
                    </div>
                    {activeYear === year && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-md shadow-md text-center">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Month</th>
                              <th className="py-2 px-4 border-b">
                                Principal Paid
                              </th>
                              <th className="py-2 px-4 border-b">
                                Interest Charged
                              </th>
                              <th className="py-2 px-4 border-b">
                                Total Payment
                              </th>
                              <th className="py-2 px-4 border-b">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {amortizationSchedule
                              .filter((item) => item.year === year)
                              .map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2 px-4 border-b">
                                    {new Date(0, item.month - 1).toLocaleString(
                                      "default",
                                      { month: "short" }
                                    )}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.principalPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.interestPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.totalPayment
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹ {item.balance.toFixed(2).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
        <div className="w-full md:w-2/3 p-4 mx-auto">
  <div className="container mx-auto py-4">
    <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
      Plan Your Canara Bank Home Loan Payments with TheFinMaster’s EMI Calculator
    </h2>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
      Buying a home is a landmark decision, one that often involves long-term financial commitment and planning. In this journey, understanding your EMI obligations clearly is the first step toward financial preparedness. That’s where TheFinMaster’s Canara Bank Home Loan EMI Calculator plays a vital role — by offering a seamless and precise way to estimate and visualise your home loan repayment journey.
      Whether you are planning to buy your first home, invest in a second property, or switch to Canara Bank for a better interest rate, this calculator empowers you with crucial insights. From calculating your EMIs in seconds to breaking down your amortisation schedule month by month, TheFinMaster ensures you never make financial decisions in the dark.
    </p>
  </div>
  <div className="container mx-auto py-4">
    <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
      What is a Home Loan EMI?
    </h2>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      A Home Loan EMI, or Equated Monthly Installment, is a fixed amount a borrower pays to the bank every month towards the repayment of their home loan. This amount comprises two essential components: interest on the outstanding principal and repayment of the loan principal itself.
      During the initial stages of repayment, a larger portion of your EMI goes toward interest, while the principal portion increases gradually as the tenure progresses. This is due to the amortisation structure of home loans. Understanding EMI breakdown helps in choosing a suitable tenure, budgeting monthly expenses, and planning for early repayments or refinancing options.
    </p>
  </div>
  <div className="container mx-auto py-4">
    <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
      What is the Canara Bank Home Loan EMI Calculator?
    </h2>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      The Canara Bank Home Loan EMI Calculator by TheFinMaster is a comprehensive and user-friendly online tool built for homeowners and loan seekers. It allows users to quickly compute their monthly repayment obligations based on three simple inputs:
    </p>
    <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      <li>Loan Amount</li>
      <li>Rate of Interest (Annual)</li>
      <li>Loan Tenure (in years or months)</li>
    </ul>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      With our calculator, you can:
    </p>
    <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      <li>Get near-instant EMI estimates</li>
      <li>Compare different loan configurations side by side</li>
      <li>See how interest and principal evolve over time</li>
      <li>Download or review a full amortisation schedule</li>
      <li>Strategize early repayment or refinancing</li>
    </ul>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      This calculator acts as a virtual advisor, helping you plan your housing loan effectively and avoid future financial strain.
    </p>
  </div>
  <div className="container mx-auto py-4">
    <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
      Why Use TheFinMaster’s Canara Bank Home Loan EMI Calculator?
    </h2>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      There are several advantages to using our EMI calculator while planning your Canara Bank home loan:
    </p>
    <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      <li><strong>Precision & Reliability:</strong> Built on standard financial formulas, our tool provides highly accurate EMI projections.</li>
      <li><strong>Time Efficiency:</strong> Skip manual calculations. Get instant results with a few clicks.</li>
      <li><strong>Scenario Planning:</strong> Modify any parameter — amount, tenure, or rate — to see real-time changes in your EMI.</li>
      <li><strong>Clear Amortisation Table:</strong> Visualise how your EMI evolves and how much interest you’re really paying.</li>
      <li><strong>No Hidden Data Collection:</strong> No login or personal details required. Fully secure and privacy-friendly.</li>
      <li><strong>Helps in Decision Making:</strong> Whether you’re comparing lenders or checking affordability, this tool gives you real-time insights.</li>
    </ul>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      The calculator is designed to give you complete control over your repayment planning, reducing financial surprises and increasing confidence in your decision-making.
    </p>
  </div>
  <div className="container mx-auto py-4">
    <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
      How Does the Canara Bank Home Loan EMI Calculator Work?
    </h2>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      The EMI is calculated using the standard formula:
    </p>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      EMI = [P × R × (1+R)^N] / [(1+R)^N – 1]
    </p>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      Where:
    </p>
    <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      <li>P = Loan Amount</li>
      <li>R = Monthly Interest Rate (Annual Interest Rate / 12 / 100)</li>
      <li>N = Tenure in Months</li>
    </ul>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      <strong>Example:</strong><br />
      Let’s say you borrow ₹55,00,000 from Canara Bank for 25 years at an interest rate of 8.30% per annum.<br />
      P = ₹55,00,000<br />
      R = 0.6916% (8.30% / 12)<br />
      N = 300 months<br />
      Calculated EMI: ₹43,427 (approx)<br />
      Total Interest Payable: ₹75,28,100<br />
      Total Repayment (Principal + Interest): ₹1,30,28,100<br />
      With this knowledge, you can decide whether the EMI is manageable or if you need to adjust your loan parameters.
    </p>
  </div>
  <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
  <div className="container mx-auto py-4">
    <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
      What is the Amortisation Schedule?
    </h2>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      An amortisation schedule provides a detailed breakup of each EMI payment across your loan tenure, showing how much of the payment is going toward interest and how much toward principal repayment. It also helps you track the reducing balance of your loan every month.
    </p>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      Benefits of an Amortisation Schedule:
    </p>
    <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      <li>Gives a complete picture of your loan lifecycle</li>
      <li>Enables smarter prepayment and refinancing decisions</li>
      <li>Highlights the best period to make lump-sum payments</li>
      <li>Offers transparency in repayment tracking</li>
    </ul>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      <strong>Sample Amortisation Schedule (First 12 Months)</strong>
    </p>
    <div className="mt-6">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-4 py-2 text-left">Month</th>
              <th className="border px-4 py-2 text-left">EMI (₹)</th>
              <th className="border px-4 py-2 text-left">Interest (₹)</th>
              <th className="border px-4 py-2 text-left">Principal (₹)</th>
              <th className="border px-4 py-2 text-left">Outstanding (₹)</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["1", "43,427", "38,075", "5,352", "54,94,648"],
              ["2", "43,427", "38,007", "5,420", "54,89,228"],
              ["3", "43,427", "37,938", "5,489", "54,83,739"],
              ["4", "43,427", "37,867", "5,560", "54,78,179"],
              ["5", "43,427", "37,796", "5,631", "54,72,548"],
              ["6", "43,427", "37,723", "5,704", "54,66,844"],
              ["7", "43,427", "37,649", "5,778", "54,61,066"],
              ["8", "43,427", "37,574", "5,853", "54,55,213"],
              ["9", "43,427", "37,498", "5,929", "54,49,284"],
              ["10", "43,427", "37,421", "6,006", "54,43,278"],
              ["11", "43,427", "37,343", "6,084", "54,37,194"],
              ["12", "43,427", "37,264", "6,163", "54,31,031"]
            ].map(([month, emi, interest, principal, balance], index) => (
              <tr key={index}>
                <td className="border px-4 py-2">{month}</td>
                <td className="border px-4 py-2">{emi}</td>
                <td className="border px-4 py-2">{interest}</td>
                <td className="border px-4 py-2">{principal}</td>
                <td className="border px-4 py-2">{balance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-4 mt-4">
        {[
          ["1", "43,427", "38,075", "5,352", "54,94,648"],
          ["2", "43,427", "38,007", "5,420", "54,89,228"],
          ["3", "43,427", "37,938", "5,489", "54,83,739"],
          ["4", "43,427", "37,867", "5,560", "54,78,179"],
          ["5", "43,427", "37,796", "5,631", "54,72,548"],
          ["6", "43,427", "37,723", "5,704", "54,66,844"],
          ["7", "43,427", "37,649", "5,778", "54,61,066"],
          ["8", "43,427", "37,574", "5,853", "54,55,213"],
          ["9", "43,427", "37,498", "5,929", "54,49,284"],
          ["10", "43,427", "37,421", "6,006", "54,43,278"],
          ["11", "43,427", "37,343", "6,084", "54,37,194"],
          ["12", "43,427", "37,264", "6,163", "54,31,031"]
        ].map(([month, emi, interest, principal, balance], index) => (
          <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
            <div><span className="font-semibold">Month:</span> {month}</div>
            <div><span className="font-semibold">EMI (₹):</span> {emi}</div>
            <div><span className="font-semibold">Interest (₹):</span> {interest}</div>
            <div><span className="font-semibold">Principal (₹):</span> {principal}</div>
            <div><span className="font-semibold">Outstanding (₹):</span> {balance}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
  <div className="container mx-auto py-4">
    <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
      Factors Affecting Your Home Loan EMI
    </h2>
    <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
      <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li><strong>Loan Amount:</strong> Larger the loan, higher the EMI. Choose the amount based on real need and affordability.</li>
        <li><strong>Interest Rate:</strong> Small changes in interest rates significantly impact your EMI. Canara Bank offers competitive rates for salaried and self-employed borrowers.</li>
        <li><strong>Tenure:</strong> A longer tenure reduces monthly EMI but increases overall interest payout. A shorter tenure means higher EMI but less interest paid.</li>
        <li><strong>Processing Fees:</strong> This upfront cost adds to your overall loan expense. Though not part of EMI, it’s crucial in loan budgeting.</li>
        <li><strong>Prepayments:</strong> Part-prepayment or foreclosure can lower your EMI or loan duration, depending on your repayment strategy.</li>
        <li><strong>CIBIL Score:</strong> A higher credit

 score often leads to better interest rates, which directly lowers your EMI.</li>
        <li><strong>Income & Occupation:</strong> Stable income and job profile affect loan eligibility, which indirectly influences how high or low your EMI can be structured.</li>
      </ol>
    </p>
  </div>
</div>

          <div className="w-full md:w-1/3 p-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                <h2 className="text-lg font-semibold ">Popular Categories</h2>
              </div>
              <div className="p-2 ">
                <ul>
                  <Link to="/financial-tools-calculators/swp-calculator">
                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                      SWP Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/mutul-fund-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Mutual Fund Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Samriddhi Yojana Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/fd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      FD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/rd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      RD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nps-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NPS Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/hra-exemption-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      HRA Exemption Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/retirement-planning-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Retirement Planning Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      EMI Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/simple-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Simple Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/compound-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Compound Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nsc-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NSC Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/step-up-sip-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Step Up Sip Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/income-tax-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Income Tax Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Home Loan Emi Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Car Loan Emi Calculator
                    </li>
                  </Link>
                  {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>

                  {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                <h2 className="text-lg font-semibold">More Categories</h2>
              </div>
              <div className="p-2">
                <ul>
                  <Link to="/financial-tools-calculators/gratuity-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Gratuity Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/APY-calculator">
                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                      APY Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/cagr-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      CAGR Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/gst-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      GST Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Flat Vs Reducing Rate Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/equity-margin-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Equity Margin Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/inflation-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Inflation Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/salary-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Salary Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/brokerage-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Brokerage Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/tds-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      TDS Calculator
                    </li>
                  </Link>
                  {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CanaraHomeLoanEmiCal;
